import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UsersIcon, SearchIcon, XIcon } from './Icons';
import { stickerTextStyle } from './StickerText';
import { Community } from '../types';

interface YourCommunitiesModalProps {
    isOpen: boolean;
    onClose: () => void;
    communities: Community[];
    onSelectCommunity: (communityId: string) => void;
}

export const YourCommunitiesModal = ({
    isOpen,
    onClose,
    communities,
    onSelectCommunity
}: YourCommunitiesModalProps) => {
    const { currentUser, usersData } = useAuth();
    const [search, setSearch] = useState('');

    if (!isOpen) return null;

    const currentUserData = usersData[currentUser || ''];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    // Filter to only include communities where the user is a member/joined
    const joinedCommunities = communities.filter(c => c.members?.includes(currentUser || ''));

    const filteredCommunities = joinedCommunities.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        (c.description && c.description.toLowerCase().includes(search.toLowerCase()))
    );

    return (
        <div 
            className="fixed inset-0 flex items-center justify-center z-[110] p-4 animate-fade-in bg-black/60 backdrop-blur-md"
            onClick={onClose}
        >
            <div 
                className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col border border-white/10 shadow-2xl max-h-[80vh]"
                onClick={e => e.stopPropagation()}
                style={{ backgroundColor: modalBg, color: userTextColor }}
            >
                {/* Header */}
                <div className="p-6 pb-4 flex items-center justify-between border-b border-white/5">
                    <h2 
                        className="text-2xl font-black tracking-tight"
                        style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                    >
                        Your Communities
                    </h2>
                    <button 
                        onClick={onClose}
                        className="p-2 bg-black/20 hover:bg-black/40 border border-white/5 hover:border-white/10 rounded-full text-white/70 hover:text-white transition-all active:scale-95"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 flex-grow overflow-y-auto custom-scrollbar space-y-4">
                    {/* Search box with soft translucent bg */}
                    <div className="relative bg-black/20 border border-white/10 rounded-2xl p-1">
                        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search joined communities..."
                            className="w-full bg-black/30 focus:border-white/30 border border-transparent rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/40 focus:outline-none text-sm transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        {filteredCommunities.length === 0 ? (
                            <div className="text-center py-8 text-white/40 text-sm">
                                {joinedCommunities.length === 0 
                                    ? "You haven't joined any communities yet." 
                                    : "No matching communities found."
                                }
                            </div>
                        ) : (
                            filteredCommunities.map(c => (
                                <button
                                    key={c._id}
                                    onClick={() => {
                                        onSelectCommunity(c._id);
                                        onClose();
                                    }}
                                    className="flex items-center gap-4 p-3 bg-black/20 border border-white/5 hover:border-white/10 rounded-2xl transition-all hover:bg-black/30 text-left w-full group active:scale-[0.98]"
                                >
                                    <div className="w-12 h-12 bg-black/30 rounded-2xl overflow-hidden flex items-center justify-center border border-white/5 group-hover:border-white/20 transition-all flex-shrink-0">
                                        {c.avatar ? (
                                            <img src={c.avatar} alt={c.name} className="w-full h-full object-cover animate-fade-in" />
                                        ) : (
                                            <UsersIcon className="w-6 h-6 text-white/50" />
                                        )}
                                    </div>
                                    <div className="flex-grow min-w-0">
                                        <p 
                                            className="font-bold text-base truncate transition-colors group-hover:text-white"
                                            style={stickerTextStyle(0.18)}
                                        >
                                            {c.name}
                                        </p>
                                        <p className="text-xs text-white/50 truncate">
                                            {c.description || `${c.members?.length || 0} members`}
                                        </p>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
